<template>
  <div class="create-vote-container">
    <div class="page-header">
      <h2>创建投票</h2>
    </div>

    <el-form
      ref="formRef"
      :model="voteForm"
      :rules="rules"
      label-width="120px"
      class="vote-form">

      <el-form-item label="投票标题" prop="title">
        <el-input v-model="voteForm.title" placeholder="请输入投票标题"/>
      </el-form-item>

      <el-form-item label="投票描述" prop="description">
        <el-input
          v-model="voteForm.description"
          type="textarea"
          :rows="4"
          placeholder="请输入投票描述"/>
      </el-form-item>

      <el-form-item label="所属类别" prop="category">
        <el-select v-model="voteForm.category" placeholder="请选择类别" class="category-select">
          <el-option
            v-for="item in categories"
            :key="item.value"
            :label="item.label"
            :value="item.value"/>
        </el-select>
      </el-form-item>

      <el-form-item label="投票选项" prop="options">
        <div v-for="(option, index) in voteForm.options"
             :key="index"
             class="option-item">
          <el-input
            v-model="option.text"
            placeholder="请输入选项内容"
            :class="{ 'is-error': optionErrors[index] }">
            <template #append>
              <el-button
                type="danger"
                :icon="Delete"
                @click="removeOption(index)"
                :disabled="voteForm.options.length <= 2"/>
            </template>
          </el-input>
          <div v-if="optionErrors[index]" class="error-message">
            {{ optionErrors[index] }}
          </div>
        </div>

        <div class="option-actions">
          <el-button
            type="link"
            :icon="Plus"
            @click="addOption"
            :disabled="voteForm.options.length >= 10">
            添加选项
          </el-button>
          <span class="option-hint">
            (最少2个选项，最多10个选项)
          </span>
        </div>
      </el-form-item>

      <el-form-item label="投票时间" prop="timeRange">
        <el-date-picker
          v-model="voteForm.timeRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          :disabled-date="disabledDate"
          :default-time="defaultTime"/>
      </el-form-item>

      <el-form-item>
        <el-button type="primary" @click="submitForm">创建投票</el-button>
        <el-button @click="resetForm">重置</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useVoteStore } from '@/stores/vote'
import { ElMessage } from 'element-plus'
import { Delete, Plus } from '@element-plus/icons-vue'

const router = useRouter()
const voteStore = useVoteStore()
const formRef = ref(null)

// 表单数据
const voteForm = reactive({
  title: '',
  description: '',
  category: '',
  options: [
    { text: '' },
    { text: '' }
  ],
  timeRange: ''
})

// 选项错误信息
const optionErrors = ref([])

// 类别选项
const categories = [
  { label: '技术', value: 'tech' },
  { label: '娱乐', value: 'entertainment' },
  { label: '生活', value: 'life' }
]

// 验证规则
const rules = {
  title: [
    { required: true, message: '请输入投票标题', trigger: 'blur' },
    { min: 3, max: 100, message: '长度在 3 到 100 个字符', trigger: 'blur' }
  ],
  description: [
    { required: true, message: '请输入投票描述', trigger: 'blur' },
    { max: 500, message: '最多 500 个字符', trigger: 'blur' }
  ],
  category: [
    { required: true, message: '请选择类别', trigger: 'change' }
  ],
  timeRange: [
    { required: true, message: '请选择投票时间', trigger: 'change' }
  ]
}

// 禁用过去的日期
const disabledDate = (time) => {
  return time.getTime() < Date.now() - 8.64e7
}

// 默认时间范围
const defaultTime = [
  new Date(2000, 1, 1, 0, 0, 0),
  new Date(2000, 1, 1, 23, 59, 59)
]

// 添加选项
const addOption = () => {
  if (voteForm.options.length < 10) {
    voteForm.options.push({ text: '' })
  }
}

// 删除选项
const removeOption = (index) => {
  if (voteForm.options.length > 2) {
    voteForm.options.splice(index, 1)
    optionErrors.value.splice(index, 1)
  }
}

// 验证选项
const validateOptions = () => {
  optionErrors.value = []
  let isValid = true
  const texts = new Set()

  voteForm.options.forEach((option, index) => {
    if (!option.text.trim()) {
      optionErrors.value[index] = '选项内容不能为空'
      isValid = false
    } else if (option.text.length > 255) {
      optionErrors.value[index] = '选项内容最多255个字符'
      isValid = false
    } else if (texts.has(option.text)) {
      optionErrors.value[index] = '选项内容不能重复'
      isValid = false
    } else {
      texts.add(option.text)
      optionErrors.value[index] = ''
    }
  })

  return isValid
}

// 提交表单
const submitForm = async () => {
  if (!validateOptions()) {
    ElMessage.error('请检查投票选项')
    return
  }

  await formRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const voteData = {
          title: voteForm.title,
          description: voteForm.description,
          category: voteForm.category,
          options: voteForm.options,
          startTime: voteForm.timeRange[0],
          endTime: voteForm.timeRange[1]
        }

        await voteStore.createVote(voteData)
        ElMessage.success('创建成功')
        router.push('/votes')
      } catch (error) {
        ElMessage.error('创建失败')
      }
    }
  })
}

// 重置表单
const resetForm = () => {
  formRef.value.resetFields()
  voteForm.options = [{ text: '' }, { text: '' }]
  optionErrors.value = []
}
</script>

<style scoped>
.create-vote-container {
  max-width: 800px;
  margin: 80px auto 20px;
  padding: 20px;
}

.page-header {
  margin-bottom: 30px;
}

.vote-form {
  background: #fff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.category-select {
  width: 100%;
}

.option-item {
  margin-bottom: 15px;
}

.option-actions {
  display: flex;
  align-items: center;
  margin-top: 10px;
}

.option-hint {
  margin-left: 10px;
  color: #909399;
  font-size: 0.9em;
}

.error-message {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 4px;
}

.is-error {
  margin-bottom: 24px;
}

:deep(.el-form-item__content) {
  flex-wrap: wrap;
}

:deep(.el-date-editor.el-input__wrapper) {
  width: 100%;
}

:deep(.el-date-editor--datetimerange) {
  width: 100%;
}
</style>
